import { ConfigProvider } from "antd";
import { APP_PREFIX_PATH, AUTH_PREFIX_PATH } from "configs/AppConfig";
import { auth } from "auth/FirebaseAuth";
import useBodyClass from "hooks/useBodyClass";
import AppLocale from "lang";
import AppLayout from "layouts/app-layout";
import AuthLayout from "layouts/auth-layout";
import React, { useEffect, useState } from "react";
import { IntlProvider } from "react-intl";
import { connect } from "react-redux";
import { Redirect, Route, Switch, withRouter } from "react-router-dom";
import { authenticated, signOutSuccess } from "redux/actions/Auth";
import { multiFactor } from "firebase/auth";
import Loading from "components/shared-components/Loading";

function RouteInterceptor({ children, condition, ...rest }) {
  return (
    <Route
      {...rest}
      render={({ location }) => {
        switch (condition) {
          case "CHILDREN":
            return children;
          case "VERIFY_EMAIL":
            return (
              <Redirect
                to={{
                  pathname: `${AUTH_PREFIX_PATH}/verify-email`,
                  state: { from: location },
                }}
              />
            );
          case "CONFIGURE_MFA":
            return (
              <Redirect
                to={{
                  pathname: `${AUTH_PREFIX_PATH}/configure-mfa`,
                  state: { from: location },
                }}
              />
            );
          case "LOGIN":
            return (
              <Redirect
                to={{
                  pathname: AUTH_PREFIX_PATH,
                  state: { from: location },
                }}
              />
            );
          default:
            return children;
        }
      }}
    />
  );
}

function AuthRouteInterceptor({ children, condition, ...rest }) {
  return (
    <Route
      {...rest}
      render={({ location }) => {
        // Se o usuário já está autenticado, redireciona para a página
        // que ele tentou acessar ou para a home.
        if (condition === "CHILDREN") {
          return (
            <Redirect
              to={{
                pathname: location.state?.from?.pathname || APP_PREFIX_PATH,
              }}
            />
          );
        }

        // Se usuário está logado mas precisa configurar MFA
        if (condition === "CONFIGURE_MFA" && !location.pathname.includes("/configure-mfa")) {
          return (
            <Redirect
              to={{
                pathname: `${AUTH_PREFIX_PATH}/configure-mfa`,
                state: { from: location },
              }}
            />
          );
        }

        // Se usuário precisa verificar email
        if (condition === "VERIFY_EMAIL" && !location.pathname.includes("/verify-email")) {
          return (
            <Redirect
              to={{
                pathname: `${AUTH_PREFIX_PATH}/verify-email`,
                state: { from: location },
              }}
            />
          );
        }

        // Se usuário não está logado e não está na página de login
        if (condition === "LOGIN" && !location.pathname.includes("/login") && location.pathname !== AUTH_PREFIX_PATH) {
          return (
            <Redirect
              to={{
                pathname: AUTH_PREFIX_PATH,
                state: { from: location },
              }}
            />
          );
        }

        // Se já está na rota correta ou é uma condição válida, renderiza normalmente
        return children;
      }}
    />
  );
}

export const Views = (props) => {
  const { locale, location, direction, condition, addUser, removeUser } = props;
  const currentAppLocale = AppLocale[locale];
  const [loading, setLoading] = useState(true)
  useBodyClass(`dir-${direction}`);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        addUser(user);
      } else {
        removeUser();
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return <Loading cover="page" />;
  }

  return (
    <IntlProvider locale={currentAppLocale.locale} messages={currentAppLocale.messages}>
      <ConfigProvider locale={currentAppLocale.antd} direction={direction}>
        <Switch>
          <Route exact path="/">
            <Redirect to={APP_PREFIX_PATH} />
          </Route>
          <AuthRouteInterceptor path={AUTH_PREFIX_PATH} condition={condition}>
            <AuthLayout direction={direction} />
          </AuthRouteInterceptor>
          <RouteInterceptor path={APP_PREFIX_PATH} condition={condition}>
            <AppLayout direction={direction} location={location} />
          </RouteInterceptor>
        </Switch>
      </ConfigProvider>
    </IntlProvider>
  );
};

const mapStateToProps = ({ theme, auth }) => {
  const { locale, direction } = theme;
  const { user } = auth;

  let condition;

  if (user && user.emailVerified) {
    try {
      const hasTotpEnrolled = multiFactor(user).enrolledFactors.length > 0;

      condition = hasTotpEnrolled ? "CHILDREN" : "CONFIGURE_MFA";
    } catch (error) {
      condition = "CONFIGURE_MFA";
    }
  }
  else if (user && !user.emailVerified)
    condition = "VERIFY_EMAIL";
  else
    condition = "LOGIN";

  return { locale, direction, condition };
};

const mapDispatchToProps = {
  addUser: authenticated,
  removeUser: signOutSuccess,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Views));
